import { CloseOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { Drawer, Button, message, Spin, Row, Col, Modal } from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import AccessProfileForm, { AccessProfileFormValues } from '../../components/AccessProfileForm';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import PageTitle from '../../components/PageTitle';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { buildPermissionsMap } from '../../utils';
import styles from './EditAccessProfile.module.css';

const EditAccessProfile = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm<AccessProfileFormValues>();
  const [setIsDirty] = useDiscardChangesModal(form);
  const { access_profile_id } = useTypedParams({ access_profile_id: Number });

  const queryClient = useQueryClient();

  const group = useAPIQuery(`/groups/${access_profile_id}/`);

  const groupUsers = useAPIQuery([
    '/users/',
    {
      params: {
        groups: access_profile_id,
      },
    },
  ]);

  const permissions = useAPIQuery(
    [
      '/permissions/',
      {
        params: {
          codename__in: [
            'view_all_vacancies',
            'create_and_edit_vacancies',
            'access_talent_pool',
            'access_configurations',
            'access_reports',
            'view_marked_request_job_postings',
            'view_all_request_job_postings',
            'create_and_edit_request_job_postings',
            'can_approve_request_job_postings',
          ].join(','),
        },
      },
    ],
    {
      select(data) {
        return buildPermissionsMap(data);
      },
    }
  );

  const [searchParams] = useSearchParams();

  const accessProfileMutation = useAPIMutation(
    async (
      {
        name,
        viewAllVacancies,
        createAndEditVacancies,
        accessTalentPool,
        accessConfigurations,
        accessReports,
        viewRequestJobPosting,
        createAndEditRequestJobPosting,
        canApproveRequestJobPosting,
      }: AccessProfileFormValues,
      api
    ) => {
      const permissions = (
        await api.get('/permissions/', {
          params: {
            codename__in: [
              'view_all_vacancies',
              'create_and_edit_vacancies',
              'access_talent_pool',
              'access_configurations',
              'access_reports',
              'view_marked_request_job_postings',
              'view_all_request_job_postings',
              'create_and_edit_request_job_postings',
              'can_approve_request_job_postings',
            ].join(','),
          },
        })
      ).data;

      const codenameIds = buildPermissionsMap(permissions);

      return api.patch(`/groups/${access_profile_id}/`, {
        name,
        permissions: [
          ...(viewAllVacancies ? [codenameIds.view_all_vacancies] : []),
          ...(createAndEditVacancies ? [codenameIds.create_and_edit_vacancies] : []),
          ...(accessTalentPool ? [codenameIds.access_talent_pool] : []),
          ...(accessConfigurations ? [codenameIds.access_configurations] : []),
          ...(accessReports ? [codenameIds.access_reports] : []),
          ...(viewRequestJobPosting === 'MARKED'
            ? [codenameIds['view_marked_request_job_postings']]
            : []),
          ...(viewRequestJobPosting === 'ALL'
            ? [codenameIds['view_all_request_job_postings']]
            : []),
          ...(createAndEditRequestJobPosting
            ? [codenameIds['create_and_edit_request_job_postings']]
            : []),
          ...(canApproveRequestJobPosting ? [codenameIds['can_approve_request_job_postings']] : []),
        ],
      });
    },
    {
      onSuccess: () => {
        setIsDirty(false);
        message.success('Perfil de acesso atualizado!');
        queryClient.invalidateQueries(`/groups/${access_profile_id}/`);
        navigate({
          pathname: '/:tenant/settings/accessManagement/accessProfiles/',
          params: { tenant },
          search: searchParams,
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries('/groups/');
        queryClient.invalidateQueries('/permissions/');
      },
      onError: (e, variables, context) => {
        if (
          e.response?.data.name &&
          Array.isArray(e.response?.data.name) &&
          e.response?.data.name.length > 0 &&
          (e.response?.data.name[0] === 'grupo com este nome já existe.' ||
            e.response?.data.name[0] === 'group with this name already exists.') // TODO: POG TEMPORARIO ATE ARRUMAR
        ) {
          message.error(t('Já existe um perfil com esse nome.'));
          return;
        }
        queryClient.defaultMutationOptions().onError?.(e, variables, context);
      },
    }
  );

  const deleteMutation = useAPIMutation((_, api) => api.delete(`/groups/${access_profile_id}/`), {
    onSuccess: () => {
      message.success('Perfil de acesso deletado!');
      navigate({
        pathname: '/:tenant/settings/accessManagement/accessProfiles/',
        params: { tenant },
        search: searchParams,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries('/groups/');
    },
  });

  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();

  const onClose = () =>
    navigate({
      pathname: '/:tenant/settings/accessManagement/accessProfiles/',
      params: { tenant },
      search: searchParams,
    });
  return (
    <Drawer
      className={styles.drawer}
      title={
        <PageTitle
          title={group.data?.name || '...'}
          level={2}
          extra={[
            <Button key="close" type="text" onClick={onClose} shape="circle">
              <CloseOutlined />
            </Button>,
          ]}
        />
      }
      maskClosable
      size="large"
      closable={false}
      width={720}
      footer={
        <Row gutter={[16, 16]}>
          <Col>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={accessProfileMutation.isLoading || deleteMutation.isLoading}
              onClick={() => form?.submit()}
            >
              {' '}
              <Trans>Salvar</Trans>
            </Button>
          </Col>
          <Col>
            <Button
              loading={accessProfileMutation.isLoading || deleteMutation.isLoading}
              onClick={onClose}
            >
              <Trans>Fechar</Trans>
            </Button>
          </Col>
          <Col flex={1} />
          <Col>
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={accessProfileMutation.isLoading || deleteMutation.isLoading}
              onClick={() => {
                if (groupUsers.data && group.data) {
                  if (groupUsers.data.results.length > 0) {
                    return Modal.warn({
                      title: t('Atenção!'),
                      content: t(
                        'O perfil de acesso {{name}} não pode ser excluído, pois já possui usuários associados. Altere o perfil dos usuários associados antes de realizar a exclusão.',
                        { name: group.data.name }
                      ),
                    });
                  }
                  deleteMutation.mutate({});
                }
              }}
            >
              {' '}
              <Trans>Deletar</Trans>
            </Button>
          </Col>
        </Row>
      }
      open
      onClose={onClose}
    >
      <Helmet>
        <title>
          {t('Editar | Perfil')} {group.data?.name || '...'} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <ErrorResult error={group.error || permissions.error}>
        <Spin spinning={group.isFetching || permissions.isFetching}>
          <AccessProfileForm
            form={form}
            initialValues={{
              name: group.data?.name,
              viewAllVacancies:
                permissions.data &&
                group.data?.permissions.includes(permissions.data.view_all_vacancies),
              createAndEditVacancies:
                permissions.data &&
                group.data?.permissions.includes(permissions.data.create_and_edit_vacancies),
              accessTalentPool:
                permissions.data &&
                group.data?.permissions.includes(permissions.data.access_talent_pool),
              accessConfigurations:
                permissions.data &&
                group.data?.permissions.includes(permissions.data.access_configurations),
              accessReports:
                permissions.data &&
                group.data?.permissions.includes(permissions.data.access_reports),
              viewRequestJobPosting:
                permissions.data &&
                group.data?.permissions.includes(permissions.data.view_all_request_job_postings)
                  ? 'ALL'
                  : permissions.data &&
                    group.data?.permissions.includes(
                      permissions.data.view_marked_request_job_postings
                    )
                  ? 'MARKED'
                  : 'FORBID',
              createAndEditRequestJobPosting:
                permissions.data &&
                group.data?.permissions.includes(
                  permissions.data.create_and_edit_request_job_postings
                ),
              canApproveRequestJobPosting:
                permissions.data &&
                group.data?.permissions.includes(permissions.data.can_approve_request_job_postings),
            }}
            layout="vertical"
            onValuesChange={() => setIsDirty(form.isFieldsTouched())}
            onFinish={(variables) => {
              accessProfileMutation.mutate(variables);
            }}
          />
        </Spin>
      </ErrorResult>
    </Drawer>
  );
};

export default withRouteChange()(EditAccessProfile);
